import React, { FC } from "react";
import PatientsTable from "../../Patient/PatientsTable";
import { useAppContext } from "../AppContext";
import Box from "../Box";
import CircularProgress from "../CircularProgress";
import Typography from "../Typography";
import useStyles from "./useStyles";
import useTranslations from "./useTranslations";
import PatientInformationDrawerContainer from "../../Patient/PatientInformationDrawerContainer";
import usePatientInformationDrawerContainer from "./usePatientInformationDrawerContainer";
import CustomerInformationDrawerContainer from "../../Customer/CustomerInformationDrawerContainer";
import useCustomerInformationDrawerContainer from "./useCustomerInformationDrawerContainer";

const SearchScreen: FC = () => {
  const { patientsSearch } = useAppContext();
  const { isLoading, patients, searchValue } = patientsSearch;
  const { searching, noResults, title } = useTranslations({ searchValue });
  const { tableRoot } = useStyles();

  const {
    isOpen: isPatientInformationDrawerContainerOpen,
    props: patientInformationDrawerContainerProps,
  } = usePatientInformationDrawerContainer();

  const customerInformationDrawerContainerProps =
    useCustomerInformationDrawerContainer();

  // Driven purely by the customerId/patientMrn route params (see
  // use{Customer,PatientInformation}DrawerContainer), independent of the
  // search results below — must render regardless of isLoading/no-results,
  // otherwise a direct link (bookmark, or the merge-target redirect in
  // useCustomer/usePatientData) can never open the drawer at all, since a
  // bare customerId/patientMrn URL with no search query yields zero
  // `patients` results. Previously these were only rendered in the
  // has-results branch below, silently breaking direct links.
  const drawers = (
    <>
      {isPatientInformationDrawerContainerOpen && (
        <PatientInformationDrawerContainer
          {...patientInformationDrawerContainerProps}
        />
      )}
      <CustomerInformationDrawerContainer
        {...customerInformationDrawerContainerProps}
      />
    </>
  );

  if (isLoading) {
    return (
      <>
        <Box
          width={1}
          display="flex"
          justifyContent="center"
          alignItems="center"
          flexDirection="column"
        >
          <CircularProgress />
          <Box mt={3}>
            <Typography variant="overline">{searching}</Typography>
          </Box>
        </Box>
        {drawers}
      </>
    );
  }

  if (!patients?.length) {
    return (
      <>
        <Box
          width={1}
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <Box mt={3}>
            <Typography variant="overline">{noResults}</Typography>
          </Box>
        </Box>
        {drawers}
      </>
    );
  }

  return (
    <Box display="flex" flexDirection="column">
      <Typography variant="h2">{title}</Typography>
      <Box mt={5} className={tableRoot}>
        <PatientsTable patients={patients} />
      </Box>
      {drawers}
    </Box>
  );
};

export default SearchScreen;
